import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate } from "react-router";
import { handlePagingAll } from "../../components/api/APIUtilities";
import moment from "moment";
import "moment-timezone";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ListResponsive from "../../components/other/ListResponsive";
import { postNotification } from "../../components/utilities/Notifications";
import {
  BCPArrowLeft,
  BCPSquarePlus,
  BCPXmark,
} from "../../components/icons/bcp/BCPIcons";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import LeaguePicker from "../../components/utilities/LeaguePicker";
import { postData } from "../../components/utilities/UtilityFunctions";
import { Formik } from "formik";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import InfoContainer from "../../components/layout/InfoContainer";
import { stringToUrl } from "../events/utils/event";

const searchLimit = 500;

export default function TOTokens() {
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState(null);

  const [tokenModal, setTokenModal] = useState(null);

  const [leagues, setLeagues] = useState(null);
  const [leagueDict, setLeagueDict] = useState({});
  const [events, setEvents] = useState(null);
  const [eventDict, setEventDict] = useState({});

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();

  const fetchLeagues = async () => {
    var filterStartDate = moment();
    var filterEndDate = moment();

    let queryParams = {
      public: true,
      active: true,
      sortKey: "name",
      "expand[]": ["gameSystem"],
    };
    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryParams.gameSystemId = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
      queryParams.gameType = getEnvironmentVariable("SINGLE_GAME_TYPE");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryParams.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    var leagueData = await handlePagingAll("/leagues", queryParams);

    if (leagueData) {
      var lDict = {};
      leagueData.forEach((league) => {
        if (
          league?.startDate &&
          moment(league.startDate).isBefore(filterStartDate)
        ) {
          filterStartDate = moment(league.startDate);
        }
        if (league.endDate && moment(league.endDate).isAfter(filterEndDate)) {
          filterEndDate = moment(league.endDate);
        }
        lDict[league.id] = league;
      });
      setLeagueDict(lDict);
      leagueData.sort((a, b) => {
        if (a.gameSystem && b.gameSystem) {
          if (
            a.gameSystem.manufacturerOrderNum !==
            b.gameSystem.manufacturerOrderNum
          )
            return (
              a.gameSystem.manufacturerOrderNum -
              b.gameSystem.manufacturerOrderNum
            );
          if (a.gameSystem.code !== b.gameSystem.code)
            return a.gameSystem.code - b.gameSystem.code;
        } else {
          if (a.gameSystem) return -1;
          if (b.gameSystem) return 1;
        }
        var nameA = a.name.toLowerCase();
        var nameB = b.name.toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
      return leagueData;
    } else return [];
  };

  const fetchTokens = async () => {
    let queryParamsTokens = {
      limit: searchLimit,
      userId: authContext?.authState?.identity?.userId,
      "expand[]": ["league", "event"],
    };

    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryParamsTokens.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
      queryParamsTokens.gameType = getEnvironmentVariable("SINGLE_GAME_TYPE");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryParamsTokens.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    const tokenData = await handlePagingAll("/leaguetokens", queryParamsTokens);

    if (tokenData?.length) {
      // Groups circuits together
      tokenData.sort((a, b) => {
        const leagueIdA = a.leagueId;
        const leagueIdB = b.leagueId;

        if (leagueIdA < leagueIdB) {
          return -1;
        } else if (leagueIdA > leagueIdB) {
          return 1;
        } else {
          return 0;
        }
      });

      let groupedArray = [];
      let currentId = "";
      let currentCircuitObj = {};
      let currentTokens = [];

      const handleLeagueChange = (leagueId) => {
        currentId = leagueId;

        // Sort tokens per circuit based on newest first
        if (currentTokens.length) {
          currentTokens.sort((a, b) => {
            const dateA = moment(a.created_at);
            const dateB = moment(b.created_at);

            if (dateA.isBefore(dateB)) return 1;
            if (dateA.isAfter(dateB)) return -1;
            return 0;
          });
        }

        if (Object.keys(currentCircuitObj).length) {
          currentCircuitObj.tokens = currentTokens;
          groupedArray.push(currentCircuitObj);
        }
        currentCircuitObj = {};
        currentTokens = [];
      };

      for (let i = 0; i < tokenData.length; i++) {
        if (currentId !== tokenData[i]?.leagueId) {
          handleLeagueChange(tokenData[i].leagueId);
        }

        if (currentCircuitObj !== tokenData[i]?.league) {
          currentCircuitObj = {
            ...tokenData[i]?.league,
          };
        }

        currentTokens.push(tokenData[i]);
      }

      handleLeagueChange(currentId);

      // Sort array based on circuit endDates, if none, those appear at the top
      groupedArray.sort((a, b) => {
        const endDate1 = a?.endDate;
        const endDate2 = b?.endDate;

        if (!endDate1 && endDate2) {
          return -1;
        } else if (endDate1 && !endDate2) {
          return 1;
        }

        if (endDate1 && endDate2) {
          const today = moment();
          const diff1 = Math.abs(moment(endDate1).diff(today));
          const diff2 = Math.abs(moment(endDate2).diff(today));

          return diff1 - diff2;
        }

        return 0;
      });

      let sortedCircuits = [];
      groupedArray.forEach((circuit) => {
        let tempCircuit = {
          ...circuit,
          isHeader: true,
        };

        if (circuit?.endDate) {
          tempCircuit.endDate = circuit.endDate;
        }

        sortedCircuits.push(tempCircuit);
        sortedCircuits.push(...circuit?.tokens);
      });

      return sortedCircuits;
    } else return [];
  };

  const parallel = async () => {
    const taskTokens = fetchTokens();
    const taskLeagues = fetchLeagues();

    return {
      tokens: await taskTokens,
      leagues: await taskLeagues,
    };
  };

  // Initial setup of data
  useEffect(() => {
    const go = async () => {
      try {
        if (!authContext?.authState?.identity?.userId) {
          navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
          return;
        }

        const parallelData = await parallel();

        if (parallelData.tokens.length > 0 && parallelData.leagues.length > 0) {
          parallelData.tokens.forEach((token) => {
            parallelData.leagues.forEach((league) => {
              if (token?.gameSystemId === league?.gameSystem?.id) {
                token.gameSystem = league.gameSystem;
              }
            });
          });
        }

        setTokens(parallelData.tokens);
        setLeagues(parallelData.leagues);
      } catch (err) {
        postNotification("error", err);
      } finally {
        setLoading(false);
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Link
              href="/organize/dashboard"
              color="link.main"
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <BCPArrowLeft
                sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
              />{" "}
              Back to Host Dashboard
            </Link>
          </Grid>

          <Grid item xs={12} md={9}>
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
            >
              My Tokens
            </Typography>
          </Grid>

          <Hidden mdDown>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={(e) => {
                  setTokenModal({});
                }}
                startIcon={<BCPSquarePlus />}
                fullWidth
              >
                New Token
              </Button>
            </Grid>
          </Hidden>

          <Grid
            item
            xs={12}
            sx={{
              p: 2,
            }}
          >
            {!loading && !tokens?.length ? (
              <InfoContainer
                title="No tokens found."
                body={
                  "You have no event tokens. Create a new token to get started!"
                }
                ActionButton={
                  <Button
                    variant="contained"
                    onClick={(e) => {
                      setTokenModal({});
                    }}
                    startIcon={<BCPSquarePlus />}
                  >
                    Create Token
                  </Button>
                }
              />
            ) : (
              <ListResponsive
                originalList={loading ? null : tokens}
                doSearch
                searchProps={{
                  label: "Search Tokens",
                  performSearch: (originalList, searchText) => {
                    if (!searchText || searchText === "") {
                      return originalList;
                    } else {
                      const filteredList = originalList.filter(function (el) {
                        if (el?.tokens?.length) {
                          for (let i = 0; i < el?.tokens?.length; i++) {
                            if (
                              el?.tokens[i]?.code
                                ?.toLowerCase()
                                .includes(searchText)
                            )
                              return true;

                            if (
                              el?.tokens[i]?.event?.name
                                ?.toLowerCase()
                                .includes(searchText)
                            )
                              return true;
                          }
                        }

                        if (el?.name?.toLowerCase().includes(searchText))
                          return true;

                        if (el?.event?.name?.toLowerCase().includes(searchText))
                          return true;

                        if (
                          el?.league?.name?.toLowerCase().includes(searchText)
                        )
                          return true;

                        if (
                          el?.league?.name?.toLowerCase().includes(searchText)
                        )
                          return true;

                        if (el?.code?.toLowerCase().includes(searchText))
                          return true;

                        if (el?.tokenType?.toLowerCase().includes(searchText))
                          return true;

                        return false;
                      });
                      return filteredList;
                    }
                  },
                }}
                actionButton={{
                  props: {
                    variant: "contained",
                    color: "primary",
                    fullWidth: true,
                    onClick: (e) => {
                      setTokenModal({});
                    },
                    sx: {
                      display: { xs: "flex", md: "none" },
                    },
                    startIcon: <BCPSquarePlus />,
                  },
                  label: "New Token",
                }}
                doPages
                initialRowsPerPage={24}
                rowProps={{
                  mapFunction: (row, index) => (
                    <TokenRow
                      token={row}
                      setTokenModal={setTokenModal}
                      theme={theme}
                      key={index}
                    />
                  ),
                }}
              />
            )}
          </Grid>
        </Grid>
      </Container>
      <TokenModal
        tokenModal={tokenModal}
        setTokenModal={setTokenModal}
        authContext={authContext}
        leagues={leagues}
        leagueDict={leagueDict}
        events={events}
        eventDict={eventDict}
        setTokens={setTokens}
        tokens={tokens}
        setLeagues={setLeagues}
        setLeagueDict={setLeagueDict}
        setEvents={setEvents}
        setEventDict={setEventDict}
      />
    </Box>
  );
}

function TokenRow(props) {
  const { token, setTokenModal, key, theme } = props;

  if (token?.isHeader) {
    return (
      <Grid item xs={12} key={key}>
        <Box
          sx={{
            backgroundColor: "table.headerBackground",
            p: 1,
            borderRadius: 1,
            color:
              theme.palette.mode === "dark"
                ? "text.primary"
                : "primary.contrastText",
          }}
        >
          <Grid container>
            <Grid item xs={12} md={6}>
              <Typography variant="h4">{token?.name}</Typography>
              <Typography variant="caption">
                {token?.gameSystem?.name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Grid item xs={12}>
                  <Typography variant="caption">
                    End date:{" "}
                    {token?.endDate
                      ? moment(token?.endDate).format("MMMM DD, YYYY")
                      : "None"}{" "}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption">
                    Start date:{" "}
                    {token?.startDate
                      ? moment(token?.startDate).format("MMMM DD, YYYY")
                      : "None"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Grid container>
        <Grid
          item
          xs={12}
          onClick={(e) => {
            if (
              token.status.toLowerCase() === "active" ||
              token.status.toLowerCase() === "pending"
            ) {
              setTokenModal({
                code: token.code,
                tokenId: token.id,
                leagueId: token.leagueId,
                eventId: token?.eventId || token?.event?.id,
                league: token.league,
                tokenType: token?.tokenType,
              });
            } else if (token.status.toLowerCase() === "used") {
              postNotification(
                "warning",
                "This Token has already been used, and cannot be associated with a new event"
              );
            } else {
              postNotification(
                "error",
                "This Token is no longer valid, and cannot be associated with a new event"
              );
            }
          }}
          sx={{
            p: 1,
            mb: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            boxShadow: 1,
            "&:hover": {
              cursor: "pointer",
            },
          }}
        >
          <Grid container>
            <Grid item xs={12} sm={8} lg={9}>
              <Typography variant="body1">
                Token
                {`${
                  token.tokenType
                    ? ` (${token.tokenType}): ${token.code}`
                    : `: ${token.code}`
                }`}
              </Typography>

              {token?.event?.name && (
                <Link
                  href={`/event/${token?.event?.id}`}
                  onClick={(e) => e.stopPropagation()}
                  underline="none"
                >
                  <Typography variant="body1">
                    Event: {token?.event?.name}
                  </Typography>
                </Link>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              lg={3}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Hidden mdDown>
                <Chip
                  label={token?.status?.toUpperCase()}
                  size="small"
                  sx={{ width: "100%" }}
                  color={
                    token.status.toLowerCase() === "active"
                      ? "success"
                      : token.status.toLowerCase() === "pending" ||
                          token.status.toLowerCase() === "used"
                        ? "warning"
                        : "error"
                  }
                />
              </Hidden>

              <Typography variant="body1">
                Issued: {moment(token?.created_at).format("MMM Do, YYYY")}
              </Typography>
              {token?.event?.eventDate && (
                <Typography variant="caption">
                  Event Date:{" "}
                  {moment(token.event.eventDate).format("MMM Do, YYYY")}
                </Typography>
              )}

              <Hidden mdUp>
                <Chip
                  label={token?.status?.toUpperCase()}
                  size="small"
                  sx={{ width: "100%" }}
                  color={
                    token.status.toLowerCase() === "active"
                      ? "success"
                      : token.status.toLowerCase() === "pending" ||
                          token.status.toLowerCase() === "used"
                        ? "warning"
                        : "error"
                  }
                />
              </Hidden>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

function TokenModal(props) {
  const {
    tokenModal,
    setTokenModal,
    leagues,
    leagueDict,
    events,
    eventDict,
    tokens,
    setTokens,
    authContext,
    setEvents,
    setEventDict,
  } = props;
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [league, setLeague] = useState(false);

  useEffect(() => {
    const check = async () => {
      setTokenData({
        tokenId: tokenModal?.tokenId || null,
        code: tokenModal?.code || "",
        leagueId: tokenModal?.leagueId || "",
        eventId: tokenModal?.eventId || "",
        tokenType: tokenModal?.tokenType || null,
      });
    };
    check();
    //eslint-disable-next-line
  }, [tokenModal]);

  useEffect(() => {
    const go = async () => {
      setEvents(null);
      setLoading(true);
      let queryStringParameters = {
        toEvents: true,
      };
      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        queryStringParameters.gameSystemId =
          getEnvironmentVariable("SINGLE_GAME_SYSTEM");
      } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
        queryStringParameters.gameType =
          getEnvironmentVariable("SINGLE_GAME_TYPE");
      } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
        queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
          "SINGLE_GAME_MANUFACTURER"
        );
      }

      if (league?.gameSystemId) {
        queryStringParameters.gameSystemId = league.gameSystemId;
      }
      if (league?.startDate) {
        queryStringParameters.startDate = league.startDate;
      }
      if (league?.endDate) {
        queryStringParameters.endDate = league.endDate;
      }

      try {
        const eventData = await handlePagingAll(
          "/events",
          queryStringParameters
        );

        var eDict = {};
        eventData.forEach((event) => {
          event.tokens = [];
          eDict[event.id] = event;
        });

        if (tokenData && tokenData.length) {
          tokenData.forEach((token) => {
            if (token.eventId && eDict[token.eventId]) {
              eDict[token.eventId].tokens.push(token);
            }
          });
        }
        setEventDict(eDict);
        eventData.sort((a, b) => {
          return moment(b.eventEndDate).diff(moment(a.eventEndDate));
        });

        setEvents(eventData);
      } catch (err) {
        postNotification("error", err);
      } finally {
        setLoading(false);
      }
    };

    go();
    //eslint-disable-next-line
  }, [league]);

  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(tokenModal)}
        onClose={() => {
          setTokenModal(null);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{ zIndex: 990 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setTokenModal(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={1}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                {tokenData?.tokenId ? "Select Event" : "New Token"}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={tokenData}
            enableReinitialize
            onSubmit={async (values) => {
              if (values.tokenId) {
                if (!values.eventId) {
                  postNotification(
                    "error",
                    "You have selected a Circuit, please select your Event"
                  );
                  return;
                }

                if (values.eventId === tokenModal.eventId) {
                  postNotification(
                    "error",
                    "This Token is already assigned to this Event"
                  );
                  return;
                }
              } else {
                if (values.code && (values.leagueId || values.eventId)) {
                  postNotification(
                    "error",
                    "Please enter a Token Code or Circuit/Event data, not both"
                  );
                  return;
                } else if (
                  !values.code &&
                  !values.leagueId &&
                  !values.eventId
                ) {
                  postNotification(
                    "error",
                    "Please enter a Token Code or Circuit/Event data"
                  );
                  return;
                } else if (values.leagueId && !values.eventId) {
                  postNotification(
                    "error",
                    "You have selected a Circuit, please select your Event"
                  );
                  return;
                }
              }
              setLoading(true);
              var newToken;
              var apiData;
              try {
                var league =
                  tokenModal?.league || leagueDict[tokenModal?.leagueId];

                if (values.tokenId) {
                  var updatedToken = await postData(
                    `/leaguetokens/${values.tokenId}/changeevent`,
                    {
                      body: {
                        eventId: values.eventId,
                      },
                    }
                  );
                  updatedToken.event = eventDict[updatedToken.eventId];
                  updatedToken.league = league;
                  let newTokens = [];
                  if (values.tokenId) {
                    tokens.forEach((token, index) => {
                      newTokens.push(
                        token.id === values.tokenId ? updatedToken : token
                      );
                    });
                  }
                  setTokens(newTokens);
                } else {
                  newToken = {
                    userId: authContext?.authState?.identity?.userId,
                    email: authContext?.authState?.identity?.email,
                  };
                  if (values.leagueId !== "") {
                    newToken.leagueId = values.leagueId;
                  }
                  if (values.eventId !== "") newToken.eventId = values.eventId;

                  apiData = await postData(`/leaguetokens`, {
                    body: newToken,
                  });

                  var tokenForList = {
                    ...newToken,
                    ...apiData,
                    id: apiData._id,
                    issued: apiData._created_at,
                    user: {
                      lastName: authContext?.authState?.identity?.lastName,
                      firstName: authContext?.authState?.identity?.firstName,
                      email: authContext?.authState?.identity?.email,
                      id: authContext?.authState?.identity?.userId,
                    },
                  };
                  if (newToken.leagueId && leagueDict[newToken.leagueId])
                    tokenForList.league = leagueDict[newToken.leagueId];
                  if (newToken.eventId && eventDict[newToken.eventId])
                    tokenForList.event = eventDict[newToken.eventId];

                  let newTokens = [];
                  if (values.tokenId) {
                    tokens.forEach((token) => {
                      newTokens.push(
                        tokenForList.id === token.id ? tokenForList : token
                      );
                    });
                  } else {
                    newTokens = [tokenForList, ...tokens];
                  }
                  setTokens(newTokens);
                }
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
              setTokenModal(null);
              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              touched,
              values,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {tokenModal?.code && (
                    <Grid item xs={12}>
                      <TextField
                        name="code"
                        label="Token Code"
                        value={values.code}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.code && errors.code)}
                        helperText={touched.code && errors.code}
                        disabled={true}
                      />
                    </Grid>
                  )}
                  {!tokenData?.tokenId && (
                    <Grid item xs={12}>
                      <LeaguePicker
                        name="leagueId"
                        label="Circuit"
                        value={values.leagueId}
                        handleChange={(e) => {
                          setLeague(leagueDict[e.target.value]);
                          setFieldValue("leagueId", e.target.value);
                        }}
                        leagues={leagues}
                        touched={touched.leagueId}
                        error={errors.leagueId}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    {values.leagueId ? (
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id={`event-label`}>Event</InputLabel>
                        <Select
                          error={Boolean(touched.eventId && errors.eventId)}
                          fullWidth
                          helperText={touched.eventId && errors.eventId}
                          labelId="event-label"
                          name="eventId"
                          value={values.eventId}
                          onChange={handleChange}
                          label="Event"
                          variant="outlined"
                        >
                          {events &&
                            events.map((event, index) => {
                              var league =
                                tokenModal?.league ||
                                leagueDict[values.leagueId];

                              if (!league) {
                                return null;
                              }
                              if (
                                league?.gameSystemId !== event?.gameSystemId
                              ) {
                                return null;
                              }
                              if (
                                league?.startDate &&
                                moment(event.eventEndDate).isBefore(
                                  league.startDate
                                )
                              ) {
                                return null;
                              }
                              if (
                                league?.endDate &&
                                moment(event.eventEndDate).isAfter(
                                  league.endDate
                                )
                              ) {
                                return null;
                              }

                              return (
                                <MenuItem key={index} value={event?.id}>
                                  {event?.name}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        disabled
                        label="Event"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      fullWidth
                      variant="outlined"
                      onClick={(e) => {
                        setTokenModal(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                  <Grid item xs={6}>
                    <Button
                      color="success"
                      fullWidth
                      variant="contained"
                      type="submit"
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
      {loading && <ModalLoadingFull />}
    </>
  );
}
